import { useState } from "react";
import { Clock, Flame, Layers } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { segmentedTabTriggerClass, segmentedTabsListClass } from "@/lib/card-styles";
import { cn } from "@/lib/utils";
import TopicListSection from "@/components/home/TopicListSection";
import type { TopicDisplaySession } from "@/components/topic/TopicCard";

interface DiscoverTab {
    key: string;
    label: string;
    icon: typeof Flame;
    title: string;
    sessions: TopicDisplaySession[];
    loading: boolean;
    isFetching: boolean;
    onShowAll: () => void;
}

interface DiscoverTabsProps {
    trending: Omit<DiscoverTab, "key" | "label" | "icon" | "title">;
    recent: Omit<DiscoverTab, "key" | "label" | "icon" | "title">;
    collections?: Omit<DiscoverTab, "key" | "label" | "icon" | "title">;
    onView: (session: TopicDisplaySession) => void;
    onPractice?: (session: TopicDisplaySession) => void;
    className?: string;
}

/**
 * Secondary content, behind tabs.
 *
 * The old home page stacked six near-identical horizontal rails down the page
 * — Trending, Recently Added, Collections, Bookmarked, Practiced, Topic of the
 * Day — all showing the same card component with the same placeholder image.
 * Scrolling that far meant seeing a lot of cards and no hierarchy.
 *
 * Tabs put the same material behind one click instead of five screens of
 * scrolling, and only one panel is mounted at a time. Each panel is a plain
 * responsive grid: no horizontal rails anywhere.
 */
const DiscoverTabs = ({
    trending,
    recent,
    collections,
    onView,
    onPractice,
    className,
}: DiscoverTabsProps) => {
    const { t } = useTranslation();
    // Titles deliberately don't echo the tab label ("Trending" / "New") —
    // repeating it one line below reads as a stutter.
    const tabs: DiscoverTab[] = [
        {
            key: "trending",
            label: t("home.discover.trending", "Trending"),
            icon: Flame,
            title: t("home.discover.trendingTitle", "Popular this week"),
            ...trending,
        },
        {
            key: "new",
            label: t("home.discover.new", "New"),
            icon: Clock,
            title: t("home.discover.newTitle", "Fresh from the community"),
            ...recent,
        },
        ...(collections
            ? [{
                key: "collections",
                label: t("home.discover.collections", "Collections"),
                icon: Layers,
                title: t("home.discover.collectionsTitle", "Curated sets"),
                ...collections,
            }]
            : []),
    ];

    const [active, setActive] = useState(tabs[0].key);

    return (
        <Tabs value={active} onValueChange={setActive} className={className}>
            <TabsList className={cn(segmentedTabsListClass, "w-fit")}>
                {tabs.map(({ key, label, icon: Icon }) => (
                    <TabsTrigger key={key} value={key} className={segmentedTabTriggerClass}>
                        <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                        {label}
                    </TabsTrigger>
                ))}
            </TabsList>

            {tabs.map(({ key, label, icon: Icon, title, sessions, loading, isFetching, onShowAll }) => (
                <TabsContent key={key} value={key} className="mt-4">
                    <TopicListSection
                        title={title}
                        icon={Icon}
                        sessions={sessions}
                        loading={loading}
                        isFetching={isFetching}
                        variant="public"
                        onShowAll={onShowAll}
                        onView={onView}
                        onPractice={onPractice}
                    />
                </TabsContent>
            ))}
        </Tabs>
    );
};

export { DiscoverTabs };
export type { DiscoverTabsProps, DiscoverTab };